"use client";

import { useRouter } from "next/navigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGraduationCap } from "@fortawesome/free-solid-svg-icons";
import { useParentStudentContext } from "@/context/ParentStudentContext";
import CheckoutCard from "@/containers/checkout/CheckoutCard";
import SubscriptionItem from "./SubscriptionItem";
import { useMemo } from "react";
import Link from "next/link";
import { SubscriptionStatus } from "@/lib/types";
import { useAuthContext } from "@/context/AuthContext";
import { useAppContext } from "@/context/AppContext";
import { useLayoutMode } from "@/hooks/useLayoutMode";

export default function ParentSubscriptionsList({
  dominant = false
}: {
  dominant?: boolean;
}) {
  const { userData } = useAuthContext();
  const { koppelingen } = useParentStudentContext();
  const { isMobile } = useAppContext();
  const { layoutMode } = useLayoutMode();
  const fullWidth = !!(isMobile || layoutMode === "native");

  const router = useRouter();

  // Sort koppelingen to show new invite at the top
  const sortedKoppelingen = useMemo(() => {
    return [...koppelingen].sort((a, b) => {
      const aHas = a.invite_token ? 1 : 0;
      const bHas = b.invite_token ? 1 : 0;
      return bHas - aHas; // items with a token first
    });
  }, [koppelingen]);

  const filteredKoppelingen = useMemo(() => {
    return sortedKoppelingen.filter(
      (koppeling) =>
        koppeling.subscription_status !== SubscriptionStatus.canceled
    );
  }, [sortedKoppelingen]);

  const hasPendingInvite = filteredKoppelingen.some((k) => k.invite_token);
  const inviteButtonClassName = hasPendingInvite
    ? "border border-mathyellow bg-white text-gray-900 py-2.5 px-5 rounded-lg font-bold hover:bg-mathyellow/10 transition-all shadow-sm"
    : "bg-mathyellow text-black py-2.5 px-6 rounded-lg font-bold hover:bg-mathyellow/90 transition-all shadow-sm";

  return (
    <>
      <CheckoutCard
        fullWidth={fullWidth}
        desktopWidth={dominant ? "wide" : "default"}
        header={
          <>
            <h2 className="mb-2 text-center text-2xl font-bold text-gray-900 md:text-3xl">
              Jouw abonnementen
            </h2>
            <p className="text-center text-sm leading-relaxed text-gray-700 md:text-base">
              {hasPendingInvite ? (
                "Deel de uitnodigingslink met je kind zodat die direct aan de slag kan met WisWiz!"
              ) : (
                <>
                  Beheer je abonnementen via je{" "}
                  <Link href="/account" className="font-semibold underline">
                    account
                  </Link>
                </>
              )}
            </p>
          </>
        }
        headerClassName="bg-mathyellow bg-opacity-20"
      >
        {filteredKoppelingen.length === 0 ? (
          userData?.pending_subscription ? (
            <p className="text-gray-600 text-center mb-6">
              We zijn je betaling aan het verwerken.
            </p>
          ) : (
            <p className="py-2 text-sm text-gray-600 text-center mb-6">
              Er zijn geen abonnementen gekoppeld aan dit account.
            </p>
          )
        ) : (
          <div className="space-y-4 mb-6 w-full">
            {filteredKoppelingen.map((koppeling, idx) => {
              return (
                <SubscriptionItem
                  key={`item_${idx}`}
                  koppeling={koppeling}
                />
              );
            })}
          </div>
        )}

        {!userData?.pending_subscription && (
          <button
            onClick={() => router.push("/checkout")}
            className={inviteButtonClassName}
          >
            <FontAwesomeIcon icon={faGraduationCap} className="mr-2" />
            {sortedKoppelingen.length === 0
              ? "Leerling uitnodigen"
              : "Nog een leerling uitnodigen"}
          </button>
        )}
      </CheckoutCard>
    </>
  );
}
